/**
 * What of the query cache survives a reload, and what only costs the operator.
 *
 * THE PROBLEM THIS ANSWERS, measured on 2026-09-08. App.tsx declared `shouldDehydrateMutation` and
 * nothing else, so `shouldDehydrateQuery` fell back to the library default —
 * `query.state.status === 'success'` — and every successful query the whole application made was
 * serialized into `localStorage` under one key. Not the inbox's queries: the client is one client
 * for the entire app, so every screen the operator opened during the shift stayed in it, kept
 * alive by a 24-hour `gcTime`, and was re-serialized on the main thread on every cache event
 * (createSyncStoragePersister's write is synchronous, throttled at 1 s). The persisted key was
 * already 174 kB after two minutes on the conversations screen alone.
 *
 * WHY PERSISTING QUERIES BUYS ALMOST NOTHING HERE, which is what makes this a technical call and
 * not a product one. App.tsx's own `onSuccess` runs `queryClient.invalidateQueries()` immediately
 * after the cache hydrates. Every hydrated query is therefore marked stale before the first paint
 * an operator could see it in, and the inbox's queries carry `staleTime` of 2-3 s with
 * `refetchInterval` of 4-6 s besides. The application already throws away what it just restored;
 * the cost is paid on the way in.
 *
 * WHAT DOES NEED TO SURVIVE is the offline send queue, and that is the mutation half. A reply the
 * operator wrote while the connection was down must still be there after a reload, which is what
 * `resumePausedMutations()` is for.
 */

import type { Query, Mutation } from '@tanstack/react-query';

/** The localStorage key the persisted cache lives under. Exported so a test — and the load
 *  harness — can measure it by name rather than by guessing. */
export const PERSISTED_CACHE_KEY = 'okos-conv-v2-cache';

/**
 * No queries are persisted.
 *
 * Declared as a predicate rather than by omission for two reasons: omitting it is what produced
 * the defect above, and a predicate is a thing a test can hold and a reader can argue with. If a
 * screen ever does need its data across a reload, this is the one place to say which.
 */
export function shouldPersistQuery(_query: Query): boolean {
  return false;
}

/**
 * Only a send that is PAUSED — queued while offline — is persisted.
 *
 * The previous predicate tested the mutation key alone, so a send that had already failed and
 * settled was written to disk with the message text inside it and could be resumed by a later
 * reload. Restoring the state test is what the library's own default does
 * (`defaultShouldDehydrateMutation = (m) => m.state.isPaused`); the key test is kept on top of it
 * so an unrelated mutation elsewhere in the app cannot start being persisted by accident.
 */
export function shouldPersistMutation(mutation: Mutation): boolean {
  return mutation.options.mutationKey?.[0] === 'send-message-v2' && mutation.state.isPaused;
}
